import { baseApi } from './baseApi';

export interface Plan {
  name: string;
  displayName: string;
  price: number;
  currency: string;
  maxUsers: number;
  cfdiStamps: number;
  features: string[];
}

/** Un extra contratado encima del plan (usuarios adicionales, etc.). */
export interface SubscriptionAddon {
  name: string;
  quantity: number;
  /** Usuarios que este extra suma al limite del plan. 0 si el extra no otorga usuarios. */
  extraUsers: number;
  /** Costo del extra por periodo (unitario x cantidad). */
  amount?: number;
  /** Periodicidad del cobro: 'month', 'year'... */
  interval?: string;
}

export interface SubscriptionStatus {
  hasSubscription: boolean;
  status: string;
  /** SIEMPRE el plan base. Los extras van en `addons`, nunca aqui. */
  planName: string;
  /** Cantidad contratada del plan base: una por sucursal (multi-restaurante). */
  planQuantity?: number;
  /** Extras contratados encima del plan. */
  addons?: SubscriptionAddon[];
  /** Suma de usuarios aportados por los extras. */
  extraUsers?: number;
  /** Lo que suman al mes los extras. Solo cuenta los de periodicidad mensual. */
  addonsMonthlyTotal?: number;
  currentPeriodEnd?: string;
  trialEnd?: string;
  /** Programada para terminar (también si se canceló desde el portal de Stripe). */
  cancelAtPeriodEnd?: boolean;
  /** Cuándo termina, si está programada para terminar. */
  cancelAt?: string;
  /** Indicates if organization has ever had a paid subscription (no trial for returning customers) */
  hasHadSubscription?: boolean;
  /** True if subscription is canceled and can be reactivated */
  canReactivate?: boolean;
}

/** Lo que ve el dueño antes de comprar usuarios adicionales. */
export interface ExtraUsersOffer {
  available: boolean;
  /** Por qué no puede comprar, ya redactado para el dueño. */
  unavailableReason?: string | null;
  /** Precio por usuario en pesos (no centavos). Sale del precio de Stripe. */
  unitAmount?: number | null;
  currency?: string | null;
  interval?: string | null;
  /** Usuarios adicionales contratados hoy. Se devuelve tal cual al comprar. */
  currentQuantity: number;
  extraUsersPerUnit: number;
  maxPerPurchase: number;
  /** La suscripción tiene un descuento que Stripe puede aplicar también a este cobro. */
  hasDiscount?: boolean;
}

export interface BuyExtraUsersRequest {
  /** Id de la compra (UUID del cliente), el mismo en cada reintento: nunca cobra dos veces. */
  purchaseId: string;
  quantity: number;
  /** Los que tenía al abrir la compra: un reintento no cobra dos veces. */
  expectedCurrentQuantity: number;
}

export interface BuyExtraUsersResponse {
  extraUsersQuantity: number;
  maxUsers: number;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  sessionUrl: string;
  clientSecret: string; // For Embedded Checkout
}

export interface StripeConfig {
  publishableKey: string;
}

// Stamp Balance types
export interface StampBalance {
  organizacionId: number;
  timbresPlanMensual: number;
  timbresPlanConsumidos: number;
  timbresPlanDisponibles: number;
  fechaResetPlan?: string;
  timbresExtra: number;
  timbresExtraConsumidos: number;
  timbresExtraDisponibles: number;
  /** Lo que esta sucursal puede timbrar de verdad: pool disponible + sus extras. */
  totalDisponibles: number;
  puedeTimbrar: boolean;
  umbralAlerta?: number;
  alertaActiva: boolean;
  /** Extras del pool antiguo. Cuentan para totalDisponibles, asi que el desglose los necesita. */
  timbresExtraPoolDisponibles?: number;
  /** Todos los extras utilizables: los de la sucursal mas los del pool antiguo. */
  timbresExtraTotalDisponibles?: number;
  /** Varias sucursales facturan con este RFC y comparten los timbres del plan. */
  esCompartido?: boolean;
  /**
   * Cuantas sucursales comparten el RFC. Puede llegar 0 o ausente si el Facturador
   * desplegado es anterior al campo: la UI no debe imprimirlo sin comprobarlo.
   */
  cuentasVinculadas?: number;
  /** Cuanto aporta esta sucursal al pool, y cuanto lleva gastado. */
  timbresPlanAportadosCuenta?: number;
  timbresPlanConsumidosCuenta?: number;
  facturacionActiva: boolean;
}

export interface StampPackage {
  id: number;
  nombre: string;
  cantidadTimbres: number;
  precio: number;
  stripePriceId?: string;
  descripcion?: string;
  orden: number;
}

export interface StampCheckoutResponse {
  sessionId: string;
  sessionUrl: string;
  clientSecret: string;
}

export const stripeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get available plans
    getPlans: builder.query<Plan[], void>({
      query: () => '/stripe/plans',
    }),

    // Get Stripe config (publishable key)
    getStripeConfig: builder.query<StripeConfig, void>({
      query: () => '/stripe/config',
    }),

    // Get current subscription status
    getSubscriptionStatus: builder.query<SubscriptionStatus, void>({
      query: () => '/stripe/subscription-status',
      providesTags: ['AppSettings'],
    }),

    // Create checkout session
    createCheckoutSession: builder.mutation<CheckoutSessionResponse, { planName: string }>({
      query: (body) => ({
        url: '/stripe/create-checkout-session',
        method: 'POST',
        body,
      }),
    }),

    // Create billing portal session
    createPortalSession: builder.mutation<{ url: string }, void>({
      query: () => ({
        url: '/stripe/create-portal-session',
        method: 'POST',
      }),
    }),

    // Cancel subscription
    cancelSubscription: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/stripe/cancel-subscription',
        method: 'POST',
      }),
      invalidatesTags: ['AppSettings'],
    }),

    // Reactivate subscription
    reactivateSubscription: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/stripe/reactivate-subscription',
        method: 'POST',
      }),
      invalidatesTags: ['AppSettings'],
    }),

    /**
     * Cambia el plan en la suscripción existente, con prorrateo. Antes "Mejorar" abría
     * un checkout nuevo que creaba una segunda suscripción: se cobraban los dos planes.
     */
    changePlan: builder.mutation<{ planName: string }, { planName: string }>({
      query: (body) => ({
        url: '/stripe/change-plan',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AppSettings', 'User'],
    }),

    /** Si se pueden comprar usuarios adicionales, a qué precio y cuántos hay. */
    getExtraUsersOffer: builder.query<ExtraUsersOffer, void>({
      query: () => '/stripe/extra-users',
      providesTags: ['AppSettings'],
    }),

    /**
     * Suma usuarios adicionales a la suscripción (con prorrateo) y sube el límite al
     * momento. 409 si la cantidad cambió desde que se consultó la oferta.
     */
    buyExtraUsers: builder.mutation<BuyExtraUsersResponse, BuyExtraUsersRequest>({
      query: (body) => ({
        url: '/stripe/extra-users',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AppSettings', 'User'],
    }),

    // ===== STAMP BALANCE ENDPOINTS =====

    // Get stamp balance for current organization
    getStampBalance: builder.query<StampBalance, void>({
      query: () => '/stripe/stamp-balance',
      providesTags: ['StampBalance'],
    }),

    // Get available stamp packages
    getStampPackages: builder.query<StampPackage[], void>({
      query: () => '/stripe/stamp-packages',
    }),

    // Create checkout session for stamp package purchase
    createStampCheckout: builder.mutation<StampCheckoutResponse, { packageName: string }>({
      query: (body) => ({
        url: '/stripe/create-stamp-checkout',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['StampBalance'],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetStripeConfigQuery,
  useGetSubscriptionStatusQuery,
  useCreateCheckoutSessionMutation,
  useCreatePortalSessionMutation,
  useCancelSubscriptionMutation,
  useReactivateSubscriptionMutation,
  useChangePlanMutation,
  useGetExtraUsersOfferQuery,
  useBuyExtraUsersMutation,
  // Stamp balance hooks
  useGetStampBalanceQuery,
  useGetStampPackagesQuery,
  useCreateStampCheckoutMutation,
} = stripeApi;
